import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Switch,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	Tooltip,
	Typography,
} from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaDownload, FaSyncAlt } from 'react-icons/fa';
import constants from '../../constants';
import {
	actionLabel,
	Area,
	AREA_LABELS,
	AREAS,
	areaOf,
	CHANNEL_LABELS,
	Channel,
	ChipColor,
	clientEventLabel,
	deliveryLabel,
	deliveryRank,
	disconnectLabel,
	eventTypeLabel,
	FACILITATOR_STEPS,
	fieldLabel,
	fmtSeconds,
	fmtToStart,
	isDeliveryFailure,
	MEMBER_STEPS,
	NORMAL_DISCONNECTS,
	reasonLabel,
	resultColor,
	resultLabel,
	statusLabel,
	StepDef,
	stepCode,
	summarizeRow,
	twilioErrorLabel,
	valueLabel,
} from './circleLogLabels';
import { cancelReasonLabel, currencyOf, PaymentChip } from './supportCirclePricing';

// GH-841 — "יומן" of a support circle (plan 5.2): everything the logs know about one circle, or about one participant
// in it. Reads GET /support-circles/admin/:id/timeline (UserActivityLog rows + support_circle_events rows, merged and
// sorted by event_at ?? timestamp on the server; the server writes ADMIN_VIEWED what=timeline on every read).
// Read-only. The logs never hold free text (reason / feedback comment / transcript) or contact details; the only
// identity here is the read-time join (pseudonym + real name) that the participants dialog already shows.

const API = `${constants.API_URL}/support-circles/admin`;
const LIMIT = 2000;
const PAGE = 400;

type Locale = { he?: string; en?: string };
export interface TimelineCircleRef {
	_id: string;
	title?: Locale;
	scheduled_at?: string;
}
interface TCircle {
	_id: string;
	title?: Locale;
	scheduled_at?: string;
	duration_minutes?: number;
	status?: string;
	started_at?: string;
	ended_at?: string;
	ended_via?: string;
	is_test?: boolean;
	is_global?: boolean;
	price?: number | null;
	facilitator?: { _id: string; name?: string } | null;
	facilitator_type?: string;
	facilitator_joined_at?: string;
	facilitator_left_at?: string;
	facilitator_notified?: { facilitator?: string; scheduled_at?: string; at?: string } | null;
	facilitator_24h_at?: string;
	facilitator_2h_at?: string;
	status_history?: { from?: string; to?: string; at?: string; by?: any; via?: string }[];
	run_summary?: Record<string, any> | null;
	/** GH-841 fix 3: LiveKit closed the room mid-slot — the circle stayed live (ended by the cron at the slot end if nobody came back) */
	room_finished_at?: string;
}
interface TParticipant {
	_id: string;
	display_name?: string;
	member?: { _id: string; first_name?: string; last_name?: string; email?: string } | null;
	registered_at?: string;
	cancelled_at?: string;
	cancel_reason?: string;
	confirmed_at?: string;
	joined_at?: string;
	left_at?: string;
	notified?: Record<string, string | undefined>;
	payment_status?: string;
	amount?: number;
	currency?: string;
	paid_at?: string;
	refunded_at?: string;
	hold_expires_at?: string;
	payment_id?: string;
	past_payments?: { payment_id?: string; amount?: number; currency?: string; status?: string; note?: string }[];
	feedback?: { rating?: number; comfort?: number; submitted_at?: string };
	// GH-841 presence fields (plan 7.1.1, LOG ONLY) — shown when the server has them
	first_joined_at?: string;
	last_joined_at?: string;
	join_count?: number;
	seconds_in_room?: number;
	last_disconnect_reason?: string;
	tokens_issued?: number;
	first_token_at?: string;
	last_token_at?: string;
	last_token_platform?: string;
	last_token_version?: string;
	removed_at?: string;
	mute_count?: number;
	last_page_poll_at?: string;
	last_room_poll_at?: string;
	page_poll_minutes_in_window?: number;
	client_last_batch_at?: string;
	client_bundle?: string;
	client_version?: string;
}
interface TDevice {
	_id?: string;
	platform?: string;
	brand?: string;
	model_name?: string;
	model_id?: string;
	os_name?: string;
	os_version?: string;
	app_version?: string;
	app_build?: string;
	browser_name?: string;
	browser_version?: string;
	last_seen_at?: string;
}

/** one merged row (a UAL row or a support_circle_events row), normalized */
interface TLRow {
	key: string;
	src: 'ual' | 'event';
	at: number;
	action?: string;
	type?: string;
	event?: string;
	result?: string;
	reason?: string;
	subReason?: string;
	participantId?: string;
	memberId?: string;
	userName?: string;
	role?: string;
	actorType?: string;
	attemptId?: string;
	platform?: string;
	version?: string;
	providerId?: string;
	meta: Record<string, any>;
	lk?: Record<string, any>;
	isStaff: boolean;
	area: Area;
	summary: string;
	raw: any;
}

const idOf = (v: any): string | undefined => {
	if (v === undefined || v === null || v === '') return undefined;
	if (typeof v === 'object') return v._id ? String(v._id) : v.$oid ? String(v.$oid) : undefined;
	return String(v);
};
const timeOf = (v: any): number | undefined => {
	if (v === undefined || v === null || v === '') return undefined;
	const t = typeof v === 'number' ? v : new Date(v).getTime();
	return Number.isFinite(t) ? t : undefined;
};
const nameOf = (m: any): string | undefined => {
	if (!m || typeof m !== 'object') return undefined;
	return [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || undefined;
};
const isObj = (v: any) => !!v && typeof v === 'object' && !Array.isArray(v);

/** the response rows — accepts { rows } / { items } / { timeline } / { docs } / { ual, events } / a bare array */
const rawRowsOf = (d: any): any[] => {
	if (Array.isArray(d)) return d;
	if (!d || typeof d !== 'object') return [];
	const list = d.rows || d.items || d.timeline || d.docs;
	if (Array.isArray(list)) return list;
	const ual = Array.isArray(d.ual) ? d.ual.map((r: any) => ({ src: 'ual', ...r })) : [];
	const ev = Array.isArray(d.events) ? d.events.map((r: any) => ({ src: 'event', ...r })) : [];
	return ual.concat(ev);
};

/**
 * GH-841 integration: the backend journal (GET /admin/:id/timeline, supportCircleTimeline.ts) returns NORMALIZED rows —
 * { id, layer: ual | event, at, logged_at, action (without the SUPPORT_CIRCLE_ prefix), type, event, result, reason,
 *   sub_reason, actor, participant_id, member_id, who, platform, version, session_id, summary_he, meta } — where meta is
 * the UAL metadata, or for an events row its data + lk + provider_id + attempt_id + session_id + installation_id +
 * bundle + browser. This turns such a row back into the stored-doc shape normalize() reads.
 */
const isServerRow = (r: any) => isObj(r) && (r.layer === 'ual' || r.layer === 'event') && isObj(r.meta);
const fromServerRow = (r: any): any => {
	const meta: Record<string, any> = { ...(r.meta || {}) };
	const byStaff = (r.actor === 'admin' || r.actor === 'staff') && typeof r.who === 'string' && r.who.includes(' · ');
	const userName = byStaff ? String(r.who).split(' · ').slice(1).join(' · ') || undefined : undefined;
	if (r.layer === 'ual') {
		const a = r.action ? String(r.action) : '';
		return {
			src: 'ual',
			_id: r.id,
			action: a ? (a.startsWith('SUPPORT_CIRCLE_') ? a : `SUPPORT_CIRCLE_${a}`) : undefined,
			result: r.result,
			userId: r.member_id || undefined,
			user_name: userName,
			timestamp: r.logged_at,
			sort_at: r.at,
			sessionId: r.session_id || undefined,
			platform: r.platform || undefined,
			version: r.version || undefined,
			metadata: {
				...meta,
				participant_id: meta.participant_id ?? r.participant_id ?? undefined,
				reason: meta.reason ?? r.reason ?? undefined,
				sub_reason: meta.sub_reason ?? r.sub_reason ?? undefined,
			},
			errorMessage: r.result && r.result !== 'SUCCESS' ? r.reason || undefined : undefined,
			summary_he: r.summary_he,
		};
	}
	const { lk, provider_id, attempt_id, session_id, installation_id, bundle, browser, ...data } = meta;
	return {
		src: 'event',
		_id: r.id,
		type: r.type || '',
		result: r.result,
		reason: r.reason || undefined,
		member: r.member_id || undefined,
		participant: r.participant_id || undefined,
		actor_type: r.actor || undefined,
		user_name: userName,
		attempt_id: attempt_id || r.session_id || undefined,
		session_id,
		installation_id,
		platform: r.platform || undefined,
		app_version: r.version || undefined,
		provider_id,
		lk: isObj(lk) ? lk : undefined,
		bundle,
		browser,
		data: { ...data, ...(r.sub_reason && !data.sub_reason ? { sub_reason: r.sub_reason } : {}) },
		event_at: r.at,
		at: r.logged_at,
		sort_at: r.at,
		summary_he: r.summary_he,
	};
};

/** GH-841 integration: the backend journal's participants[] ({ participant_id, member_id, presence{…}, card{…} }) → the
 *  presence fields merged onto the admin read's participants (by _id), and card.device → devices[memberId] */
const serverPresenceOf = (list: any[]): Record<string, TParticipant> => {
	const out: Record<string, TParticipant> = {};
	(Array.isArray(list) ? list : []).forEach((p: any) => {
		if (!isObj(p)) return;
		if (p._id) {
			out[String(p._id)] = p; // a stored participant doc (older / other shape)
			return;
		}
		if (!p.participant_id) return;
		const fields: Record<string, any> = { _id: String(p.participant_id) };
		Object.entries(isObj(p.presence) ? p.presence : {}).forEach(([k, v]) => {
			if (v !== null && v !== undefined) fields[k] = v;
		});
		out[String(p.participant_id)] = fields as TParticipant;
	});
	return out;
};
const serverDevicesOf = (list: any[]): Record<string, TDevice[]> => {
	const out: Record<string, TDevice[]> = {};
	(Array.isArray(list) ? list : []).forEach((p: any) => {
		if (isObj(p) && p.member_id && isObj(p.card?.device)) out[String(p.member_id)] = [p.card.device];
	});
	return out;
};

const normalize = (rawIn: any, i: number, memberToParticipant: Record<string, string>): TLRow => {
	const raw = isServerRow(rawIn) ? fromServerRow(rawIn) : rawIn;
	const kind = String(raw.src || raw.kind || raw.source_kind || raw.store || '');
	const isUal = kind === 'ual' || (kind !== 'event' && kind !== 'events' && !!raw.action);
	const meta: Record<string, any> = isObj(isUal ? raw.metadata : raw.data) ? { ...(isUal ? raw.metadata : raw.data) } : {};
	const memberId = isUal ? idOf(raw.userId) : idOf(raw.member);
	const participantId =
		(isUal ? idOf(meta.participant_id) : idOf(raw.participant)) ||
		idOf(raw.participant_id) ||
		(memberId ? memberToParticipant[memberId] : undefined);
	const type = isUal ? undefined : String(raw.type || '');
	const at =
		timeOf(raw.sort_at) ??
		(isUal
			? timeOf(meta.event_at) ?? timeOf(raw.timestamp) ?? timeOf(raw.at) ?? timeOf(raw.createdAt)
			: timeOf(raw.event_at) ?? timeOf(raw.at) ?? timeOf(raw.createdAt)) ??
		0;
	const action = isUal ? String(raw.action || '') : type && type.startsWith('SUPPORT_CIRCLE_') ? type : undefined;
	const event = isUal ? meta.event : type && type.startsWith('client:') ? type.slice(7) : meta.event;
	const reason = (isUal ? meta.reason : raw.reason ?? meta.reason) ?? (raw.result && raw.result !== 'SUCCESS' ? raw.errorMessage : undefined);
	const lk = isObj(raw.lk) ? raw.lk : undefined;
	const row: TLRow = {
		key: String(raw._id || `${isUal ? 'u' : 'e'}-${i}`),
		src: isUal ? 'ual' : 'event',
		at,
		action: action || undefined,
		type: isUal ? undefined : type,
		event: event ? String(event) : undefined,
		result: raw.result ? String(raw.result) : undefined,
		reason: reason ? String(reason) : undefined,
		subReason: meta.sub_reason ? String(meta.sub_reason) : raw.sub_reason ? String(raw.sub_reason) : undefined,
		participantId,
		memberId,
		userName: raw.user_name || raw.member_name || nameOf(raw.userId) || nameOf(raw.member),
		role: meta.role || raw.role || (lk?.identity_kind === 'facilitator' ? 'facilitator' : undefined),
		actorType: raw.actor_type ? String(raw.actor_type) : undefined,
		attemptId: (isUal ? raw.sessionId || meta.attempt_id : raw.attempt_id || meta.attempt_id) || undefined,
		platform: raw.platform || meta.platform || undefined,
		version: raw.version || raw.app_version || meta.app_version || undefined,
		providerId: raw.provider_id || meta.provider_id || undefined,
		meta,
		lk,
		isStaff: meta.actor_is_staff === true,
		area: 'system',
		summary: '',
		raw,
	};
	if (!isUal && raw.status && !row.meta.status) row.meta.status = raw.status;
	row.area = areaOf({ action: row.action, type: row.type, event: row.event, meta: row.meta });
	row.summary = summarizeRow(
		{ action: row.action, type: row.type, event: row.event, result: row.result, reason: row.reason, subReason: row.subReason, meta: row.meta, lk: row.lk },
		raw.summary_he,
	);
	return row;
};

const isFacilitatorRow = (r: TLRow) =>
	r.role === 'facilitator' ||
	r.role === 'staff_host' ||
	r.actorType === 'facilitator' ||
	r.lk?.identity_kind === 'facilitator' ||
	r.lk?.identity_kind === 'staff' ||
	r.meta.recipient_role === 'facilitator' ||
	/^F\d/.test(stepCode(r.meta.step));
const isClientRow = (r: TLRow) => !!r.action?.startsWith('SUPPORT_CIRCLE_CLIENT_') || !!r.type?.startsWith('client:');
/** the device / browser of a row — UAL metadata (os / browser / in_app / build, from the request ctx), the client
 *  telemetry ctx (os_name / in_app_browser / app_build) or the support_circle_events columns (browser / bundle) */
const devOf = (r: TLRow) => {
	const m = r.meta;
	const inApp = m.in_app || m.in_app_browser;
	return {
		os: [m.os || m.os_name, m.os_version].filter(Boolean).join(' '),
		browser: [m.browser || r.raw?.browser, m.browser_version].filter(Boolean).join(' '),
		inApp: inApp && inApp !== 'none' ? String(inApp) : '',
		build: m.build || m.app_build || '',
		bundle: m.bundle || r.raw?.bundle || '',
	};
};
const hasDev = (r: TLRow) => {
	const d = devOf(r);
	return !!(r.platform || r.version || d.os || d.browser);
};
const isFail = (r: TLRow) => !!r.result && r.result !== 'SUCCESS';
const deliveryStatusOf = (r: TLRow): string | undefined => {
	const s = r.meta.status || (r.action === 'SUPPORT_CIRCLE_NOTIFICATION_DELIVERY' ? r.reason : undefined);
	return s ? String(s) : undefined;
};
const isDeliveryRow = (r: TLRow) =>
	r.action === 'SUPPORT_CIRCLE_NOTIFICATION_DELIVERY' ||
	(r.src === 'event' && r.area === 'notifications' && !!r.meta.status && !String(r.type).includes('rejected'));
const isNotificationRow = (r: TLRow) => r.action === 'SUPPORT_CIRCLE_NOTIFICATION';

const JOIN_FAIL_ACTIONS = [
	'SUPPORT_CIRCLE_AUTH_REFUSED',
	'SUPPORT_CIRCLE_JOIN_TOKEN',
	'SUPPORT_CIRCLE_TOKEN_NOT_USED',
	'SUPPORT_CIRCLE_ROOM_CONNECTION_ABORTED',
	'SUPPORT_CIRCLE_CLIENT_CONNECT',
	'SUPPORT_CIRCLE_CLIENT_MEDIA',
];
/** jwt_expired followed by a success of the same member within a minute = a normal token refresh (plan 2.6.1) */
const recoveredAuth = (r: TLRow, all: TLRow[]) =>
	r.reason === 'jwt_expired' &&
	(typeof r.meta.recovered_within_s === 'number' ||
		all.some((x) => !!x.memberId && x.memberId === r.memberId && x.result === 'SUCCESS' && x.at > r.at && x.at - r.at <= 60000));
/** GH-841 integration: an auth refusal of the client's own telemetry POST (route_kind client_events) is not a failed
 *  attempt to enter (the same expired login also refuses the circle page / token — those are the join signal) */
const isTelemetryAuth = (r: TLRow) => r.action === 'SUPPORT_CIRCLE_AUTH_REFUSED' && r.meta.route_kind === 'client_events';
const isJoinFailure = (r: TLRow, all: TLRow[]) => !!r.action && JOIN_FAIL_ACTIONS.includes(r.action) && isFail(r) && !recoveredAuth(r, all) && !isTelemetryAuth(r);
const failKey = (r: TLRow) => String(r.reason || r.meta.reason_name || r.event || r.action || '');
const failKeyLabel = (k: string) => (reasonLabel(k) !== k ? reasonLabel(k) : clientEventLabel(k) !== k ? clientEventLabel(k) : actionLabel(k));

const countBy = (keys: string[]) => {
	const m: Record<string, number> = {};
	keys.forEach((k) => {
		if (k) m[k] = (m[k] || 0) + 1;
	});
	return Object.entries(m).sort((a, b) => b[1] - a[1]);
};
const distinctCount = (rows: TLRow[]) => Array.from(new Set(rows.map((r) => r.participantId).filter(Boolean))).length;

function ResultChip({ result }: { result?: string }) {
	if (!result) return null;
	return <Chip size="small" color={resultColor(result)} variant={result === 'SUCCESS' ? 'outlined' : 'filled'} label={resultLabel(result)} sx={{ height: 20, fontSize: 11 }} />;
}

function Fact({ label, value, color }: { label: string; value: ReactNode; color?: string }) {
	return (
		<Box sx={{ minWidth: 120, px: 1.5, py: 1, border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#fafafa' }}>
			<Typography variant="caption" color="text.secondary" display="block">
				{label}
			</Typography>
			<Typography variant="body2" sx={{ fontWeight: 600, color }}>
				{value}
			</Typography>
		</Box>
	);
}

/** "דנה (p…a1)" — pseudonym + short id (+ the real name when asked) */
const pLabel = (p: TParticipant | undefined, withName = true) => {
	if (!p) return '';
	const real = nameOf(p.member);
	return `${p.display_name || 'משתתף/ת'}${withName && real ? ` (${real})` : ''}`;
};

export default function SupportCircleTimeline({
	open,
	circle,
	participantId,
	onClose,
}: {
	open: boolean;
	circle: TimelineCircleRef | null;
	participantId?: string | null;
	onClose: () => void;
}) {
	const [pid, setPid] = useState<string>(participantId || '');
	const [tab, setTab] = useState<string>(participantId ? 'participant' : 'overview');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [response, setResponse] = useState<any>(null);
	const [base, setBase] = useState<{ circleId: string; circle: TCircle; participants: TParticipant[] } | null>(null);
	const [area, setArea] = useState<Area | 'all'>('all');
	const [failuresOnly, setFailuresOnly] = useState(false);
	const [withTelemetry, setWithTelemetry] = useState(true);
	const [withViews, setWithViews] = useState(false);
	const [shown, setShown] = useState(PAGE);
	const [expanded, setExpanded] = useState<string | null>(null);

	// every open starts from what the caller asked for (the whole circle, or one participant)
	useEffect(() => {
		if (!open) return;
		setPid(participantId || '');
		setTab(participantId ? 'participant' : 'overview');
		setArea('all');
		setFailuresOnly(false);
		setShown(PAGE);
		setExpanded(null);
	}, [open, participantId, circle?._id]);

	const reqRef = useRef(0); // only the latest read may land (switching participant quickly = out-of-order replies)
	const load = useCallback(async () => {
		if (!circle?._id) return;
		const my = ++reqRef.current;
		setLoading(true);
		setError('');
		try {
			const q = new URLSearchParams({ limit: String(LIMIT) });
			if (pid) q.set('participant', pid);
			const { data } = await axios.get(`${API}/${circle._id}/timeline?${q.toString()}`);
			// circle + the full participants list: from the circle-level timeline when it carries them, otherwise (or for a
			// participant-level read, which may carry only that participant) from the regular admin read, once per circle
			let b = base && base.circleId === circle._id ? base : null;
			// (GH-841 integration: the backend journal's participants carry participant_id + presence + card, not the stored
			// docs with notified markers / payments — those still come from the admin read below)
			if (!b && !pid && data && isObj(data.circle) && Array.isArray(data.participants) && data.participants.every((p: any) => isObj(p) && !!p._id))
				b = { circleId: circle._id, circle: data.circle, participants: data.participants };
			if (!b) {
				const res = await axios.get(`${API}/${circle._id}`);
				b = { circleId: circle._id, circle: res.data || { _id: circle._id }, participants: res.data?.participants || [] };
			}
			if (my !== reqRef.current) return;
			setResponse(data);
			setBase(b);
		} catch (e: any) {
			if (my !== reqRef.current) return;
			const st = e?.response?.status;
			setResponse(null);
			setError(
				st === 404
					? 'המעגל לא נמצא, או שהשרת עוד לא כולל את היומן (GH-841)'
					: st === 403 || st === 401
					? 'אין הרשאה לצפות ביומן'
					: e?.response?.data?.message || 'טעינת היומן נכשלה',
			);
		} finally {
			if (my === reqRef.current) setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [circle?._id, pid]);
	useEffect(() => {
		if (open && circle?._id) load();
	}, [open, circle?._id, pid, load]);

	const close = () => {
		reqRef.current += 1; // a read still in flight must not land after closing
		setResponse(null);
		setBase(null);
		setError('');
		onClose();
	};

	// ---------------------------------------------------------------- derived data
	const c: TCircle = useMemo(
		() => ({ ...(circle || { _id: '' }), ...(base?.circle || {}), ...(isObj(response?.circle) ? response.circle : {}) }) as TCircle,
		[circle, base, response],
	);
	const participants: TParticipant[] = useMemo(() => {
		const list = base?.participants || [];
		// presence fields the timeline may add on its participants (plan 7.1.1)
		const extra: Record<string, TParticipant> = serverPresenceOf(response?.participants);
		const merged = list.map((p) => ({ ...p, ...(extra[String(p._id)] || {}) }));
		Object.keys(extra).forEach((id) => {
			if (!merged.some((p) => String(p._id) === id)) merged.push(extra[id]);
		});
		return merged;
	}, [base, response]);
	const pById = useMemo(() => {
		const m: Record<string, TParticipant> = {};
		participants.forEach((p) => (m[String(p._id)] = p));
		return m;
	}, [participants]);
	const memberToParticipant = useMemo(() => {
		const m: Record<string, string> = {};
		participants.forEach((p) => {
			const mid = idOf(p.member);
			if (mid && !m[mid]) m[mid] = String(p._id);
		});
		return m;
	}, [participants]);
	const rows: TLRow[] = useMemo(
		() =>
			rawRowsOf(response)
				.map((r, i) => normalize(r, i, memberToParticipant))
				.sort((a, b) => a.at - b.at),
		[response, memberToParticipant],
	);
	const devices: Record<string, TDevice[]> = useMemo(() => (isObj(response?.devices) ? response.devices : serverDevicesOf(response?.participants)), [response]);
	const truncated = !!response?.truncated || rows.length >= LIMIT;
	const sameDay = (t: number) => !!c.scheduled_at && moment(t).isSame(moment(c.scheduled_at), 'day');
	const fmtT = (t?: number | string) => {
		const ms = typeof t === 'string' ? timeOf(t) : t;
		if (!ms) return '—';
		return moment(ms).format(sameDay(ms) ? 'HH:mm:ss' : 'DD/MM HH:mm:ss');
	};
	const facilitatorName = c.facilitator?.name || (c.facilitator ? 'מנחה' : 'צוות GroupHug');
	const whoOf = (r: TLRow) => {
		if (r.area === 'admin') return `אדמין${r.userName ? `: ${r.userName}` : ''}`;
		if (isFacilitatorRow(r) && r.meta.as !== 'target') return r.role === 'staff_host' ? `צוות מנחה${r.userName ? `: ${r.userName}` : ''}` : `מנחה · ${facilitatorName}`;
		if (r.participantId && pById[r.participantId]) return pLabel(pById[r.participantId], !pid);
		if (r.userName) return r.userName;
		if (r.actorType && !['member', 'system'].includes(r.actorType))
			return ({ livekit: 'LiveKit', payme: 'PayMe', twilio: 'Twilio', sendgrid: 'SendGrid', agent: 'סוכן', guest: 'אורח/ת', admin: 'אדמין', staff: 'צוות' } as Record<string, string>)[r.actorType] || r.actorType;
		return r.memberId ? 'מנוי/ה' : 'מערכת';
	};

	const visibleRows = useMemo(
		() =>
			rows.filter((r) => {
				if (failuresOnly && !isFail(r)) return false;
				if (!withTelemetry && r.src === 'event' && r.type?.startsWith('client:')) return false;
				if (!withViews && r.action === 'SUPPORT_CIRCLE_ADMIN_VIEWED') return false;
				if (area === 'all') return true;
				if (area === 'facilitator') return r.area === 'facilitator' || isFacilitatorRow(r);
				return r.area === area;
			}),
		[rows, failuresOnly, withTelemetry, withViews, area],
	);

	// ---------------------------------------------------------------- notification cells
	const cellFor = (forRows: TLRow[], step: StepDef, channel: Channel, marker?: string, stepsExpected?: string[]) => {
		const sent = forRows.filter((r) => isNotificationRow(r) && stepCode(r.meta.step) === step.code && (r.meta.channel || 'email') === channel);
		const tips: string[] = [];
		if (!sent.length) {
			if (Array.isArray(stepsExpected) && !stepsExpected.includes(step.code) && !stepsExpected.includes(step.step))
				return { label: 'לא רלוונטי', color: 'default' as ChipColor, variant: 'outlined' as const, tips: ['נרשם/ה אחרי החלון של השלב הזה (steps_expected)'] };
			if (marker)
				return { label: 'סומן', color: 'default' as ChipColor, variant: 'outlined' as const, tips: [`סומן כנשלח ${fmtT(marker)} — אין שורת לוג (לפני הלוגים, או ערוץ אחר)`] };
			return null;
		}
		const last = sent[sent.length - 1];
		const m = last.meta;
		tips.push(`${fmtT(last.at)} · ${resultLabel(last.result)}${sent.length > 1 ? ` · ${sent.length} שורות` : ''}`);
		if (m.template_key) tips.push(`תבנית: ${m.template_key}${m.variant ? ` (${m.variant})` : ''}`);
		if (m.date_text || m.time_text) tips.push(`הטקסט שנשלח: "${[m.date_text, m.time_text].filter(Boolean).join(' ')}"${m.tz ? ` ${m.tz}` : ''}`);
		if (m.email_domain || m.email_target) tips.push(`מייל: ${[valueLabel('email_target', m.email_target), m.email_domain].filter(Boolean).join(' · ')}`);
		if (m.phone_last4) tips.push(`טלפון: ${m.phone_cc ? `+${String(m.phone_cc).replace(/^\+/, '')} ` : ''}…${m.phone_last4}`);
		if (last.providerId) tips.push(`מזהה ספק: ${last.providerId}`);
		if (m.dry_run) tips.push('dry run (סביבת פיתוח)');
		if (last.result === 'FAILURE') return { label: `דולג: ${reasonLabel(last.reason)}`, color: 'warning' as ChipColor, variant: 'outlined' as const, tips };
		if (last.result === 'ERROR') {
			if (m.error_code) tips.push(twilioErrorLabel(m.error_code));
			if (m.error_message || m.message) tips.push(String(m.error_message || m.message));
			return { label: `נכשל${m.error_code ? ` ${m.error_code}` : ''}`, color: 'error' as ChipColor, variant: 'filled' as const, tips };
		}
		const deliveries = forRows.filter(
			(r) =>
				isDeliveryRow(r) &&
				((!!last.providerId && r.providerId === last.providerId) ||
					(!r.providerId && stepCode(r.meta.step) === step.code && (r.meta.channel || channel) === channel && r.at >= last.at)),
		);
		let best: TLRow | undefined;
		deliveries.forEach((d) => {
			tips.push(`${fmtT(d.at)} ${deliveryLabel(deliveryStatusOf(d))}${d.meta.error_code ? ` · ${twilioErrorLabel(d.meta.error_code)}` : ''}`);
			if (!best || deliveryRank(deliveryStatusOf(d)) >= deliveryRank(deliveryStatusOf(best))) best = d;
		});
		const st = best ? deliveryStatusOf(best) : undefined;
		if (st && isDeliveryFailure(st))
			return { label: `${deliveryLabel(st)}${best?.meta.error_code ? ` ${best.meta.error_code}` : ''}`, color: 'error' as ChipColor, variant: 'filled' as const, tips };
		if (st === 'read') return { label: 'נקרא', color: 'success' as ChipColor, variant: 'filled' as const, tips };
		if (st === 'delivered') return { label: 'נמסר', color: 'success' as ChipColor, variant: 'outlined' as const, tips };
		if (channel === 'whatsapp' && !st) tips.push('אין עדיין סטטוס מסירה מ-Twilio');
		return { label: st ? deliveryLabel(st) : 'נשלח', color: 'info' as ChipColor, variant: 'outlined' as const, tips };
	};
	const cellChip = (cell: ReturnType<typeof cellFor>) =>
		cell ? (
			<Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{cell.tips.join('\n')}</span>}>
				<Chip size="small" color={cell.color} variant={cell.variant} label={cell.label} sx={{ height: 20, fontSize: 11, maxWidth: 150 }} />
			</Tooltip>
		) : (
			<Typography variant="caption" color="text.disabled">
				—
			</Typography>
		);
	const stepsExpectedOf = (pRows: TLRow[]): string[] | undefined => {
		const reg = pRows.filter((r) => r.action === 'SUPPORT_CIRCLE_REGISTERED' && r.result === 'SUCCESS' && Array.isArray(r.meta.steps_expected));
		return reg.length ? (reg[reg.length - 1].meta.steps_expected as string[]) : undefined;
	};
	const memberColumns = MEMBER_STEPS.reduce<{ step: StepDef; channel: Channel }[]>((acc, s) => acc.concat(s.channels.map((ch) => ({ step: s, channel: ch }))), []);

	const notificationMatrix = (list: TParticipant[]) => (
		<TableContainer component={Paper} variant="outlined">
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell align="right">משתתף/ת</TableCell>
						{memberColumns.map(({ step, channel }) => (
							<TableCell key={`${step.code}-${channel}`} align="center" sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
								{step.code} {step.label}
								<br />
								<Typography variant="caption" color="text.secondary">
									{CHANNEL_LABELS[channel]}
								</Typography>
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{list.map((p) => {
						const pRows = rows.filter((r) => r.participantId === String(p._id));
						const expected = stepsExpectedOf(pRows);
						return (
							<TableRow key={p._id} sx={{ opacity: p.cancelled_at ? 0.6 : 1 }}>
								<TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
									{pLabel(p, !pid)}
									{p.cancelled_at ? (
										<Typography variant="caption" color="text.secondary" display="block">
											{cancelReasonLabel(p.cancel_reason)}
										</Typography>
									) : null}
								</TableCell>
								{memberColumns.map(({ step, channel }) => (
									<TableCell key={`${step.code}-${channel}`} align="center">
										{cellChip(cellFor(pRows, step, channel, step.marker ? p.notified?.[step.marker] ?? (step.legacyMarker ? p.notified?.[step.legacyMarker] : undefined) : undefined, expected))}
									</TableCell>
								))}
							</TableRow>
						);
					})}
					{list.length === 0 && (
						<TableRow>
							<TableCell colSpan={memberColumns.length + 1} align="center">
								<Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
									אין נרשמים.
								</Typography>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);

	// ---------------------------------------------------------------- compact list of rows (cards)
	const miniRows = (list: TLRow[], empty: string) =>
		list.length ? (
			<Box sx={{ fontSize: 13, lineHeight: 1.8 }}>
				{list.map((r) => (
					<Stack key={r.key} direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
						<Typography component="span" variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 62 }}>
							{fmtT(r.at)}
						</Typography>
						<ResultChip result={r.result} />
						<Typography component="span" variant="body2">
							{r.summary}
						</Typography>
						{(r.platform || r.version) && (
							<Typography component="span" variant="caption" color="text.secondary">
								{[r.platform, r.version].filter(Boolean).join(' ')}
							</Typography>
						)}
					</Stack>
				))}
			</Box>
		) : (
			<Typography variant="body2" color="text.secondary">
				{empty}
			</Typography>
		);

	// ---------------------------------------------------------------- metrics strip (circle level)
	const metrics = useMemo(() => {
		// KPI without staff (actor_is_staff) — test circles are flagged in the header instead
		const staffP = new Set<string>();
		rows.forEach((r) => {
			if (r.isStaff && r.participantId && (!r.role || r.role === 'participant')) staffP.add(r.participantId);
		});
		const kpiP = participants.filter((p) => !staffP.has(String(p._id)));
		const kpiIds = new Set(kpiP.map((p) => String(p._id)));
		const rk = rows.filter((r) => !r.isStaff && !!r.participantId && kpiIds.has(r.participantId as string) && !isFacilitatorRow(r));
		const active = kpiP.filter((p) => !p.cancelled_at && p.payment_status !== 'pending');
		const joinedIds = new Set(
			rk
				.filter((r) => r.action === 'SUPPORT_CIRCLE_ROOM_JOINED' && r.result !== 'FAILURE')
				.map((r) => r.participantId as string)
				.concat(kpiP.filter((p) => !!p.joined_at || !!p.first_joined_at).map((p) => String(p._id))),
		);
		const failRows = rk.filter((r) => isJoinFailure(r, rows));
		const failedIds = Array.from(new Set(failRows.map((r) => r.participantId as string)));
		const noShowRows = rk.filter((r) => r.action === 'SUPPORT_CIRCLE_NO_SHOW');
		const ended = c.status === 'ended' || (!!c.scheduled_at && moment().isAfter(moment(c.scheduled_at).add(c.duration_minutes || 60, 'minutes')));
		const noShow = noShowRows.length ? distinctCount(noShowRows) : ended ? active.filter((p) => !joinedIds.has(String(p._id))).length : undefined;
		const facJoin = rows.find((r) => r.action === 'SUPPORT_CIRCLE_ROOM_JOINED' && isFacilitatorRow(r));
		const facJoinedAt = timeOf(c.facilitator_joined_at) ?? facJoin?.at;
		const started = timeOf(c.started_at);
		const endedAt = timeOf(c.ended_at);
		const ratings = kpiP.map((p) => p.feedback?.rating).filter((x): x is number => typeof x === 'number' && x > 0);
		return {
			staffCount: staffP.size,
			registered: active.length,
			cancelled: kpiP.filter((p) => !!p.cancelled_at).length,
			confirmed: active.filter((p) => !!p.confirmed_at).length,
			tokens: distinctCount(rk.filter((r) => r.action === 'SUPPORT_CIRCLE_JOIN_TOKEN' && r.result === 'SUCCESS')),
			joined: Array.from(joinedIds).filter((id) => kpiIds.has(id)).length,
			failedAny: failedIds.length,
			failedNever: failedIds.filter((id) => !joinedIds.has(id)).length,
			failReasons: countBy(failRows.map(failKey)),
			noShow,
			noShowDerived: !noShowRows.length && noShow !== undefined,
			facJoinedAt,
			duration: started && endedAt ? Math.round((endedAt - started) / 60000) : undefined,
			paid: kpiP.filter((p) => p.payment_status === 'paid').length,
			refundFailed: kpiP.filter((p) => p.payment_status === 'refund_failed').length,
			avgRating: ratings.length ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : undefined,
			errors: rows.filter((r) => r.result === 'ERROR').length,
		};
	}, [rows, participants, c]);

	// ---------------------------------------------------------------- participant card
	const participantCard = (p: TParticipant) => {
		const mine = rows.filter((r) => r.participantId === String(p._id));
		const tokensOk = mine.filter((r) => r.action === 'SUPPORT_CIRCLE_JOIN_TOKEN' && r.result === 'SUCCESS');
		const tokensBad = mine.filter((r) => r.action === 'SUPPORT_CIRCLE_JOIN_TOKEN' && isFail(r));
		const auth = mine.filter((r) => r.action === 'SUPPORT_CIRCLE_AUTH_REFUSED');
		const joins = mine.filter((r) => r.action === 'SUPPORT_CIRCLE_ROOM_JOINED' && r.result !== 'FAILURE');
		const lefts = mine.filter((r) => r.action === 'SUPPORT_CIRCLE_ROOM_LEFT');
		const secondsFromRows = lefts.reduce((s, r) => s + (typeof r.meta.stay_seconds === 'number' ? r.meta.stay_seconds : 0), 0);
		const summaryRow = mine.filter((r) => r.action === 'SUPPORT_CIRCLE_ATTENDANCE_SUMMARY').pop();
		const seconds =
			typeof p.seconds_in_room === 'number' ? p.seconds_in_room : secondsFromRows || (typeof summaryRow?.meta.total_min === 'number' ? summaryRow.meta.total_min * 60 : 0);
		const disconnects = countBy(
			lefts
				.map((r) => {
					const dr = String(r.meta.disconnect_reason || r.reason || '');
					return dr ? `${disconnectLabel(dr)}${r.meta.dup_kind ? ` (${valueLabel('dup_kind', r.meta.dup_kind)})` : ''}` : '';
				})
				.concat(
					mine
						.filter((r) => r.action === 'SUPPORT_CIRCLE_CLIENT_DISCONNECTED')
						.map((r) => `אצל המשתמש: ${disconnectLabel(r.meta.reason_name) || valueLabel('exit_path', r.meta.exit_path) || 'ניתוק'}`),
				),
		);
		const abnormal = lefts.some((r) => r.meta.disconnect_reason && !NORMAL_DISCONNECTS.includes(String(r.meta.disconnect_reason)));
		const withDevice = mine.filter(hasDev);
		const lastDev = withDevice[withDevice.length - 1];
		const dv = lastDev ? devOf(lastDev) : undefined;
		const clientRows = mine.filter(isClientRow);
		const lastClientAt = timeOf(p.client_last_batch_at) ?? (clientRows.length ? clientRows[clientRows.length - 1].at : undefined);
		const devs = devices[idOf(p.member) || ''] || [];
		const refusals = mine.filter((r) => isFail(r) && r.area !== 'notifications');
		// attempts = attempt_id (token → client connect → disconnect)
		const attemptIds: string[] = [];
		const byAttempt: Record<string, TLRow[]> = {};
		mine.forEach((r) => {
			if (!r.attemptId) return;
			if (!byAttempt[r.attemptId]) {
				byAttempt[r.attemptId] = [];
				attemptIds.push(r.attemptId);
			}
			byAttempt[r.attemptId].push(r);
		});
		return (
			<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
				<Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
						{p.display_name || 'משתתף/ת'}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{[nameOf(p.member), p.member?.email].filter(Boolean).join(' · ') || '—'}
					</Typography>
					<PaymentChip p={p} fallbackCurrency={currencyOf(c)} />
					{p.cancelled_at ? <Chip size="small" label={`ההרשמה בוטלה ${cancelReasonLabel(p.cancel_reason)}`} /> : null}
					{p.removed_at ? <Chip size="small" color="warning" label={`הוסר/ה ע"י המנחה ${fmtT(p.removed_at)}`} /> : null}
					<Typography variant="caption" color="text.secondary">
						p…{String(p._id).slice(-4)}
					</Typography>
				</Stack>
				<Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
					<Fact label="נרשם/ה" value={p.registered_at ? moment(p.registered_at).format('DD/MM HH:mm') : '—'} />
					<Fact label="אישר/ה הגעה" value={p.confirmed_at ? moment(p.confirmed_at).format('DD/MM HH:mm') : 'לא'} />
					<Fact
						label="טוקנים"
						value={`${tokensOk.length || p.tokens_issued || 0} הונפקו${tokensBad.length ? ` · ${tokensBad.length} נדחו` : ''}`}
						color={tokensBad.length && !joins.length ? '#b45309' : undefined}
					/>
					<Fact label="סירובי auth" value={auth.length ? `${auth.length} (${countBy(auth.map((r) => reasonLabel(r.reason))).map(([k, v]) => `${k} ×${v}`).join(', ')})` : '0'} />
					<Fact label="כניסות לחדר" value={joins.length || p.join_count || (p.joined_at ? 1 : 0)} />
					<Fact label="זמן בחדר" value={seconds ? fmtSeconds(seconds) : '—'} />
					<Fact
						label="הקליינט דיווח"
						value={lastClientAt ? `לאחרונה ${fmtT(lastClientAt)}` : 'לא דיווח בכלל'}
						color={!lastClientAt && (tokensOk.length || tokensBad.length) ? '#b45309' : undefined}
					/>
					{p.feedback?.submitted_at ? <Fact label="משוב" value={[p.feedback.rating ? `★ ${p.feedback.rating}/5` : '', p.feedback.comfort ? `נוחות ${p.feedback.comfort}/5` : ''].filter(Boolean).join(' · ') || 'נשלח'} /> : null}
				</Stack>
				<Stack spacing={1}>
					<Box>
						<Typography variant="caption" color="text.secondary">
							מכשיר / דפדפן (אחרון):{' '}
						</Typography>
						<Typography component="span" variant="body2">
							{lastDev && dv
								? [
										lastDev.platform,
										lastDev.version,
										dv.build ? `build ${dv.build}` : '',
										lastDev.meta.version_source ? `(גרסה ${valueLabel('version_source', lastDev.meta.version_source)})` : '',
										dv.os,
										dv.browser,
										dv.inApp ? `דפדפן בתוך ${dv.inApp}` : '',
										dv.bundle || p.client_bundle ? `bundle ${dv.bundle || p.client_bundle}` : '',
								  ]
										.filter(Boolean)
										.join(' · ')
								: [p.last_token_platform, p.last_token_version].filter(Boolean).join(' ') || 'לא ידוע'}
						</Typography>
					</Box>
					{devs.length > 0 && (
						<Box>
							<Typography variant="caption" color="text.secondary">
								מכשירים רשומים (member_devices):{' '}
							</Typography>
							<Typography component="span" variant="body2">
								{devs
									.slice(0, 3)
									.map((d) =>
										[
											[d.brand, d.model_name || d.model_id].filter(Boolean).join(' ') || d.platform,
											[d.os_name, d.os_version].filter(Boolean).join(' '),
											d.app_version ? `אפליקציה ${d.app_version}${d.app_build ? ` (${d.app_build})` : ''}` : '',
											[d.browser_name, d.browser_version].filter(Boolean).join(' '),
										]
											.filter(Boolean)
											.join(' '),
									)
									.join(' | ')}
							</Typography>
						</Box>
					)}
					{disconnects.length > 0 && (
						<Box>
							<Typography variant="caption" color="text.secondary">
								ניתוקים:{' '}
							</Typography>
							{disconnects.map(([k, v]) => (
								<Chip key={k} size="small" variant="outlined" color={abnormal ? 'warning' : 'default'} label={`${k} ×${v}`} sx={{ ml: 0.5, mb: 0.5, height: 20, fontSize: 11 }} />
							))}
						</Box>
					)}
					{refusals.length > 0 && (
						<Box>
							<Typography variant="caption" color="text.secondary">
								סירובים ותקלות:{' '}
							</Typography>
							{countBy(refusals.map((r) => `${actionLabel(r.action) || eventTypeLabel(r.type)}: ${failKeyLabel(failKey(r))}`)).map(([k, v]) => (
								<Chip key={k} size="small" color="warning" variant="outlined" label={`${k}${v > 1 ? ` ×${v}` : ''}`} sx={{ ml: 0.5, mb: 0.5, height: 20, fontSize: 11 }} />
							))}
						</Box>
					)}
				</Stack>
				{attemptIds.length > 0 && (
					<>
						<Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
							ניסיונות כניסה
						</Typography>
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell align="right">ניסיון</TableCell>
										<TableCell align="right">זמן</TableCell>
										<TableCell align="right">מכשיר</TableCell>
										<TableCell align="right">טוקן</TableCell>
										<TableCell align="right">חיבור</TableCell>
										<TableCell align="right">סיום</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{attemptIds.map((id) => {
										const list = byAttempt[id];
										const tk = list.filter((r) => r.action === 'SUPPORT_CIRCLE_JOIN_TOKEN').pop();
										const conn = list.filter((r) => r.action === 'SUPPORT_CIRCLE_CLIENT_CONNECT' || r.type === 'client:connected' || r.type === 'client:connect_failed');
										const okConn = conn.find((r) => r.event === 'connected');
										const badConn = conn.filter((r) => isFail(r)).pop();
										const disc = list.filter((r) => r.action === 'SUPPORT_CIRCLE_CLIENT_DISCONNECTED').pop();
										const dev = list.find((r) => r.platform || r.version);
										return (
											<TableRow key={id}>
												<TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
													{id.slice(0, 8)}
												</TableCell>
												<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
													{fmtT(list[0].at)}
												</TableCell>
												<TableCell align="right">{dev ? [dev.platform, dev.version].filter(Boolean).join(' ') : '—'}</TableCell>
												<TableCell align="right">
													{tk ? (
														<Stack direction="row" spacing={0.5} alignItems="center">
															<ResultChip result={tk.result} />
															<span>{tk.result === 'SUCCESS' ? fmtToStart(tk.meta.minutes_to_start) : reasonLabel(tk.reason)}</span>
														</Stack>
													) : (
														'—'
													)}
												</TableCell>
												<TableCell align="right">
													{okConn ? `התחבר/ה${typeof okConn.meta.connect_ms === 'number' ? ` (${fmtSeconds(okConn.meta.connect_ms / 1000)})` : ''}` : badConn ? `נכשל: ${failKeyLabel(failKey(badConn))}` : '—'}
												</TableCell>
												<TableCell align="right">
													{disc ? disconnectLabel(disc.meta.reason_name) || valueLabel('exit_path', disc.meta.exit_path) || 'התנתק/ה' : '—'}
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				)}
			</Paper>
		);
	};

	// ---------------------------------------------------------------- facilitator card
	const facilitatorCard = () => {
		const facRows = rows.filter((r) => isFacilitatorRow(r) && r.meta.as !== 'target');
		const notif = facRows.filter((r) => isNotificationRow(r) || isDeliveryRow(r));
		const markers: Record<string, string | undefined> = {
			F1: c.facilitator_notified?.at,
			F2: c.facilitator_24h_at,
			F3: c.facilitator_2h_at,
		};
		const tokens = facRows.filter((r) => r.action === 'SUPPORT_CIRCLE_JOIN_TOKEN');
		const facMemberIds = Array.from(new Set(tokens.map((r) => String(r.meta.facilitator_member_id || r.memberId || '')).filter(Boolean)));
		const auth = rows.filter((r) => r.action === 'SUPPORT_CIRCLE_AUTH_REFUSED' && !!r.memberId && facMemberIds.includes(r.memberId));
		const room = facRows.filter((r) => r.area === 'room' || r.type?.startsWith('lk:'));
		const actions = rows.filter(
			(r) => ['SUPPORT_CIRCLE_STARTED', 'SUPPORT_CIRCLE_ENDED', 'SUPPORT_CIRCLE_FACILITATOR_NO_SHOW'].includes(r.action || '') || r.type === 'observer_dispatch' || ((r.action === 'SUPPORT_CIRCLE_PARTICIPANT_REMOVED' || r.action === 'SUPPORT_CIRCLE_PARTICIPANT_MUTED') && r.meta.as !== 'target'),
		);
		const startRow = actions.filter((r) => r.action === 'SUPPORT_CIRCLE_STARTED' && r.result === 'SUCCESS')[0];
		const joinedAt = timeOf(c.facilitator_joined_at) ?? room.find((r) => r.action === 'SUPPORT_CIRCLE_ROOM_JOINED')?.at;
		return (
			<Paper variant="outlined" sx={{ p: 2 }}>
				<Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
					מנחה · {facilitatorName}
					{c.facilitator_type === 'ai' ? ' · AI' : ''}
				</Typography>
				<Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
					{FACILITATOR_STEPS.map((s) => (
						<Box key={s.code} sx={{ textAlign: 'center' }}>
							<Typography variant="caption" color="text.secondary" display="block">
								{s.code} {s.label}
							</Typography>
							{cellChip(cellFor(notif, s, 'email', markers[s.code]))}
						</Box>
					))}
					<Fact
						label="נכנס/ה לחדר"
						value={joinedAt ? `${fmtT(joinedAt)}${c.scheduled_at ? ` · ${fmtToStart((moment(c.scheduled_at).valueOf() - joinedAt) / 60000)}` : ''}` : 'לא נכנס/ה'}
						color={!joinedAt && c.scheduled_at && moment().isAfter(moment(c.scheduled_at)) && c.status !== 'cancelled' ? '#b91c1c' : undefined}
					/>
					<Fact
						label="התחיל/ה"
						value={
							startRow
								? `${fmtT(startRow.at)}${typeof startRow.meta.waiting_in_lobby === 'number' ? ` · ${startRow.meta.waiting_in_lobby} בלובי` : ''}`
								: c.started_at
								? fmtT(c.started_at)
								: '—'
						}
					/>
					<Fact label="טוקנים" value={`${tokens.filter((r) => r.result === 'SUCCESS').length} הונפקו${tokens.filter(isFail).length ? ` · ${tokens.filter(isFail).length} נדחו` : ''}`} />
					<Fact label="סירובי auth" value={auth.length} />
					{tokens.some((r) => r.meta.other_installation_recent) ? <Fact label="אזהרה" value="כמה מכשירים עם אותה זהות" color="#b45309" /> : null}
				</Stack>
				<Typography variant="subtitle2">כניסה, חדר ופעולות</Typography>
				{miniRows(tokens.concat(auth, room, actions).sort((a, b) => a.at - b.at), 'אין עדיין שורות של המנחה.')}
			</Paper>
		);
	};

	// ---------------------------------------------------------------- admin audit + payments
	const fmtVal = (field: string, v: any): string => {
		if (v === undefined || v === null || v === '') return 'ריק';
		if (typeof v === 'boolean') return v ? 'כן' : 'לא';
		if (field === 'status') return statusLabel(String(v));
		if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return moment(v).format('DD/MM/YYYY HH:mm');
		if (typeof v === 'object') {
			try {
				const s = JSON.stringify(v);
				return s.length > 80 ? `${s.slice(0, 80)}…` : s;
			} catch {
				return '…';
			}
		}
		return String(v);
	};
	const changesView = (meta: Record<string, any>) => {
		const ch = isObj(meta.changes) ? (meta.changes as Record<string, any>) : {};
		const keys = Object.keys(ch);
		if (!keys.length) return <>{Array.isArray(meta.changed_fields) && meta.changed_fields.length ? meta.changed_fields.map(fieldLabel).join(', ') : '—'}</>;
		return (
			<Box sx={{ fontSize: 12.5, lineHeight: 1.7 }}>
				{keys.map((f) => {
					const v = ch[f] || {};
					let text: string;
					if ('from_len' in v || 'to_len' in v) text = `שונה (אורך ${v.from_len ?? '?'} → ${v.to_len ?? '?'})`;
					else if ('added' in v || 'removed' in v)
						text = [Array.isArray(v.added) && v.added.length ? `נוספו ${v.added.length}` : '', Array.isArray(v.removed) && v.removed.length ? `הוסרו ${v.removed.length}` : '']
							.filter(Boolean)
							.join(' · ') || 'שונה';
					else if ('from' in v || 'to' in v) text = `${fmtVal(f, v.from)} → ${fmtVal(f, v.to)}`;
					else text = fmtVal(f, v);
					return (
						<div key={f}>
							<strong>{fieldLabel(f)}:</strong> {text}
						</div>
					);
				})}
			</Box>
		);
	};
	const auditTable = () => {
		const list = rows.filter((r) => r.area === 'admin' && (withViews || r.action !== 'SUPPORT_CIRCLE_ADMIN_VIEWED'));
		return (
			<>
				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
						פעולות אדמין
					</Typography>
					<FormControlLabel control={<Switch size="small" checked={withViews} onChange={(e) => setWithViews(e.target.checked)} />} label="כולל צפיות" />
				</Stack>
				{list.length === 0 && !(c.status_history || []).length ? (
					<Typography variant="body2" color="text.secondary">
						אין שורות אדמין.
					</Typography>
				) : (
					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell align="right">זמן</TableCell>
									<TableCell align="right">מי</TableCell>
									<TableCell align="right">פעולה</TableCell>
									<TableCell align="right">תוצאה</TableCell>
									<TableCell align="right">לפני ← אחרי</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{list.map((r) => (
									<TableRow key={r.key}>
										<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
											{moment(r.at).format('DD/MM HH:mm:ss')}
										</TableCell>
										<TableCell align="right">{r.userName || '—'}</TableCell>
										<TableCell align="right">{actionLabel(r.action)}</TableCell>
										<TableCell align="right">
											<ResultChip result={r.result} /> {isFail(r) ? reasonLabel(r.reason) : ''}
										</TableCell>
										<TableCell align="right">
											{r.action === 'SUPPORT_CIRCLE_ADMIN_STATUS_CHANGED' ? (
												`${statusLabel(r.meta.from)} → ${statusLabel(r.meta.to)}${r.meta.refunds ? ` · החזרים: ${fmtVal('refunds', r.meta.refunds)}` : ''}`
											) : r.action === 'SUPPORT_CIRCLE_ADMIN_VIEWED' ? (
												valueLabel('what', r.meta.what)
											) : r.action === 'SUPPORT_CIRCLE_ADMIN_UPDATED' ? (
												changesView(r.meta)
											) : (
												r.summary
											)}
										</TableCell>
									</TableRow>
								))}
								{(c.status_history || []).map((h, i) => (
									<TableRow key={`sh-${i}`}>
										<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
											{h.at ? moment(h.at).format('DD/MM HH:mm:ss') : '—'}
										</TableCell>
										<TableCell align="right">{nameOf(h.by) || (h.by ? String(idOf(h.by) || '') : '—')}</TableCell>
										<TableCell align="right">היסטוריית סטטוס</TableCell>
										<TableCell align="right">{valueLabel('via', h.via) || valueLabel('ended_via', h.via)}</TableCell>
										<TableCell align="right">{`${statusLabel(h.from)} → ${statusLabel(h.to)}`}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</>
		);
	};
	const paymentsTable = (list: TLRow[]) =>
		list.length === 0 ? (
			<Typography variant="body2" color="text.secondary">
				אין שורות תשלום.
			</Typography>
		) : (
			<TableContainer component={Paper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="right">זמן</TableCell>
							<TableCell align="right">מי</TableCell>
							<TableCell align="right">מה קרה</TableCell>
							<TableCell align="right">תוצאה</TableCell>
							<TableCell align="right">תשלום</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{list.map((r) => (
							<TableRow key={r.key}>
								<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
									{fmtT(r.at)}
								</TableCell>
								<TableCell align="right">{whoOf(r)}</TableCell>
								<TableCell align="right">{r.summary}</TableCell>
								<TableCell align="right">
									<ResultChip result={r.result} />
								</TableCell>
								<TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
									{r.meta.payment_id || r.providerId || '—'}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		);

	// ---------------------------------------------------------------- timeline table
	const timelineTable = () => (
		<>
			<Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
				<Chip size="small" label={`הכל (${rows.length})`} color={area === 'all' ? 'primary' : 'default'} onClick={() => setArea('all')} />
				{AREAS.map((a) => {
					const count = a === 'facilitator' ? rows.filter((r) => r.area === 'facilitator' || isFacilitatorRow(r)).length : rows.filter((r) => r.area === a).length;
					return count ? <Chip key={a} size="small" label={`${AREA_LABELS[a]} (${count})`} color={area === a ? 'primary' : 'default'} onClick={() => setArea(a)} /> : null;
				})}
				<FormControlLabel control={<Switch size="small" checked={failuresOnly} onChange={(e) => setFailuresOnly(e.target.checked)} />} label="רק כישלונות" />
				<FormControlLabel control={<Switch size="small" checked={withTelemetry} onChange={(e) => setWithTelemetry(e.target.checked)} />} label="כולל טלמטריה גולמית" />
				<FormControlLabel control={<Switch size="small" checked={withViews} onChange={(e) => setWithViews(e.target.checked)} />} label="כולל צפיות אדמין" />
			</Stack>
			<TableContainer component={Paper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="right">זמן</TableCell>
							<TableCell align="right">מי</TableCell>
							<TableCell align="right">תחום</TableCell>
							<TableCell align="right">מה קרה</TableCell>
							<TableCell align="right">תוצאה</TableCell>
							<TableCell align="right">מכשיר</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{visibleRows.slice(0, shown).map((r) => {
							const bg = r.result === 'ERROR' ? '#fef2f2' : r.result === 'FAILURE' ? '#fffbeb' : undefined;
							return (
								<Fragment key={r.key}>
									<TableRow hover sx={{ cursor: 'pointer', bgcolor: bg }} onClick={() => setExpanded(expanded === r.key ? null : r.key)}>
										<TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
											{fmtT(r.at)}
										</TableCell>
										<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
											{whoOf(r)}
										</TableCell>
										<TableCell align="right">
											<Typography variant="caption" color="text.secondary">
												{AREA_LABELS[r.area]}
												{r.src === 'event' ? ' · אירוע' : ''}
											</Typography>
										</TableCell>
										<TableCell align="right">{r.summary}</TableCell>
										<TableCell align="right">
											<ResultChip result={r.result} />
										</TableCell>
										<TableCell align="right">
											<Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
												{[r.platform, r.version, devOf(r).browser, devOf(r).inApp ? `in-app ${devOf(r).inApp}` : '']
													.filter(Boolean)
													.join(' · ')}
											</Typography>
										</TableCell>
									</TableRow>
									{expanded === r.key && (
										<TableRow>
											<TableCell colSpan={6} sx={{ bgcolor: '#f8fafc' }}>
												<Collapse in>
													<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
														{r.action || r.type}
														{r.reason ? ` · reason=${r.reason}` : ''}
														{r.subReason ? ` · sub_reason=${r.subReason}` : ''}
														{r.attemptId ? ` · attempt=${r.attemptId}` : ''}
														{r.meta.request_id ? ` · rid=${r.meta.request_id}` : ''}
													</Typography>
													<Box component="pre" dir="ltr" sx={{ m: 0, fontSize: 11.5, maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'left' }}>
														{(() => {
															try {
																return JSON.stringify(r.raw, null, 2);
															} catch {
																return '';
															}
														})()}
													</Box>
												</Collapse>
											</TableCell>
										</TableRow>
									)}
								</Fragment>
							);
						})}
						{visibleRows.length === 0 && (
							<TableRow>
								<TableCell colSpan={6} align="center">
									<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
										{rows.length ? 'אין שורות שמתאימות לסינון.' : 'אין עדיין שורות לוג למעגל הזה.'}
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			{visibleRows.length > shown && (
				<Box sx={{ textAlign: 'center', mt: 1 }}>
					<Button size="small" onClick={() => setShown(shown + PAGE)}>
						הצג עוד ({visibleRows.length - shown})
					</Button>
				</Box>
			)}
		</>
	);

	const download = () => {
		if (!response || !circle) return;
		try {
			const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
			const a = document.createElement('a');
			a.href = URL.createObjectURL(blob);
			a.download = `circle-${circle._id}${pid ? `-p${pid.slice(-6)}` : ''}-timeline.json`;
			a.click();
			setTimeout(() => URL.revokeObjectURL(a.href), 5000);
		} catch {
			// nothing to do — the button just doesn't download
		}
	};

	const selected = pid ? pById[pid] : undefined;
	const title = c.title?.he || c.title?.en || circle?.title?.he || circle?.title?.en || '';
	const m = metrics;

	return (
		<Dialog open={open} onClose={close} maxWidth="xl" fullWidth dir="rtl">
			<DialogTitle sx={{ pb: 1 }}>
				<Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
					<span>
						יומן — {title}
						{selected ? ` · ${selected.display_name || 'משתתף/ת'}` : ''}
					</span>
					{c.status ? <Chip size="small" label={statusLabel(c.status)} /> : null}
					{c.is_test ? <Chip size="small" color="secondary" label="מעגל בדיקה" /> : null}
					{c.scheduled_at ? (
						<Typography variant="body2" color="text.secondary">
							{moment(c.scheduled_at).format('DD/MM/YYYY HH:mm')}
							{c.duration_minutes ? ` · ${c.duration_minutes} דק׳` : ''}
						</Typography>
					) : null}
				</Stack>
			</DialogTitle>
			<DialogContent>
				<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
					<FormControl size="small" sx={{ minWidth: 260 }}>
						<InputLabel>הצג</InputLabel>
						<Select
							label="הצג"
							value={pid}
							onChange={(e) => {
								const v = String(e.target.value || '');
								setPid(v);
								setTab(v ? 'participant' : 'overview');
								setShown(PAGE);
								setExpanded(null);
							}}
						>
							<MenuItem value="">כל המעגל</MenuItem>
							{/* opened on one participant before the list loaded — keep the value a known option */}
							{pid && !pById[pid] ? <MenuItem value={pid}>טוען…</MenuItem> : null}
							{participants.map((p) => (
								<MenuItem key={p._id} value={String(p._id)}>
									{pLabel(p)}
									{p.cancelled_at ? ' — בוטל' : ''}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Button size="small" variant="outlined" startIcon={<FaSyncAlt />} onClick={load} disabled={loading}>
						רענון
					</Button>
					<Tooltip title="כל השורות כמו שהשרת החזיר (לבדיקה מעמיקה)">
						<span>
							<Button size="small" startIcon={<FaDownload />} onClick={download} disabled={!response}>
								הורדת JSON
							</Button>
						</span>
					</Tooltip>
					{loading && <CircularProgress size={18} />}
					<Typography variant="caption" color="text.secondary">
						{rows.length} שורות · כל פתיחה של היומן נרשמת (SOC2)
					</Typography>
				</Stack>
				{error && (
					<Alert severity="warning" sx={{ mb: 1 }}>
						{error}
					</Alert>
				)}
				{truncated && (
					<Alert severity="info" sx={{ mb: 1 }}>
						מוצגות {rows.length} השורות הראשונות. לפירוט מלא — הסקריפט circle-timeline.ts או הורדת JSON.
					</Alert>
				)}
				{c.is_test && (
					<Alert severity="info" sx={{ mb: 1 }}>
						מעגל בדיקה — השורות שלו לא נספרות במדדים ובדוחות.
					</Alert>
				)}

				<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
					{pid ? <Tab value="participant" label="משתתף/ת" /> : <Tab value="overview" label="סקירה" />}
					<Tab value="timeline" label={`ציר זמן (${visibleRows.length})`} />
					<Tab value="notifications" label="התראות" />
					{!pid && <Tab value="participants" label={`משתתפים (${participants.length})`} />}
					<Tab value="payments" label="תשלומים" />
					{!pid && <Tab value="admin" label="אדמין" />}
				</Tabs>

				{tab === 'overview' && !pid && (
					<Stack spacing={2}>
						<Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
							<Fact label="נרשמו" value={`${m.registered}${m.cancelled ? ` (+${m.cancelled} ביטלו)` : ''}`} />
							<Fact label="אישרו הגעה" value={m.confirmed} />
							<Fact label="ביקשו טוקן" value={m.tokens} />
							<Fact label="נכנסו" value={m.joined} />
							<Fact label="נכשלו בכניסה" value={m.failedAny ? `${m.failedAny} (${m.failedNever} לא נכנסו בסוף)` : 0} color={m.failedNever ? '#b45309' : undefined} />
							<Fact label="לא הגיעו" value={m.noShow === undefined ? '—' : `${m.noShow}${m.noShowDerived ? ' (מחושב)' : ''}`} />
							<Fact
								label="המנחה נכנס/ה"
								value={m.facJoinedAt ? `${fmtT(m.facJoinedAt)}${c.scheduled_at ? ` · ${fmtToStart((moment(c.scheduled_at).valueOf() - m.facJoinedAt) / 60000)}` : ''}` : 'לא'}
								color={!m.facJoinedAt && c.scheduled_at && moment().isAfter(moment(c.scheduled_at)) && c.status !== 'cancelled' ? '#b91c1c' : undefined}
							/>
							<Fact label="זמן אמיתי" value={m.duration !== undefined ? `${m.duration} דק׳` : '—'} />
							{(Number(c.price) || 0) > 0 ? <Fact label="שילמו" value={`${m.paid}${m.refundFailed ? ` · ${m.refundFailed} החזרים נכשלו` : ''}`} color={m.refundFailed ? '#b91c1c' : undefined} /> : null}
							{m.avgRating ? <Fact label="דירוג ממוצע" value={`★ ${m.avgRating}`} /> : null}
							<Fact label="תקלות (ERROR)" value={m.errors} color={m.errors ? '#b91c1c' : undefined} />
							{c.ended_via ? <Fact label="הסתיים דרך" value={valueLabel('ended_via', c.ended_via)} /> : null}
							{c.room_finished_at ? <Fact label="החדר התרוקן באמצע הזמן" value={fmtT(c.room_finished_at)} color="#b45309" /> : null}
						</Stack>
						{m.failReasons.length > 0 && (
							<Box>
								<Typography variant="caption" color="text.secondary">
									סיבות כישלון בכניסה:{' '}
								</Typography>
								{m.failReasons.map(([k, v]) => (
									<Chip key={k} size="small" color="warning" variant="outlined" label={`${failKeyLabel(k)} ×${v}`} sx={{ ml: 0.5, mb: 0.5 }} />
								))}
							</Box>
						)}
						<Typography variant="caption" color="text.secondary">
							המדדים בלי צוות GroupHug{m.staffCount ? ` (${m.staffCount} משתתפי צוות הוחרגו)` : ''}. "נכשלו בכניסה" לא כולל רענון תקין של התחברות.
						</Typography>
						{facilitatorCard()}
						{(() => {
							const sys = rows.filter((r) => r.area === 'system' && isFail(r));
							return sys.length ? (
								<Paper variant="outlined" sx={{ p: 2 }}>
									<Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
										תקלות מערכת
									</Typography>
									{miniRows(sys, '')}
								</Paper>
							) : null;
						})()}
					</Stack>
				)}
				{tab === 'participant' && pid && (selected ? participantCard(selected) : <Typography color="text.secondary">המשתתף/ת לא נמצא/ה.</Typography>)}
				{tab === 'timeline' && timelineTable()}
				{tab === 'notifications' && (
					<Stack spacing={2}>
						{notificationMatrix(selected ? [selected] : participants)}
						<Typography variant="caption" color="text.secondary">
							נשלח = הספק קיבל · נמסר / נקרא = סטטוס מ-Twilio (WhatsApp) · דולג = לא נשלח ולמה · לא רלוונטי = נרשם/ה אחרי החלון · סומן = יש סימון על ההרשמה ואין שורת לוג.
							מעבר עם העכבר מציג את הטקסט שנשלח, התבנית והקוד.
						</Typography>
						{!pid && facilitatorCard()}
					</Stack>
				)}
				{tab === 'participants' && !pid && (
					<Box>
						{participants.map((p) => (
							<Fragment key={p._id}>{participantCard(p)}</Fragment>
						))}
						{participants.length === 0 && (
							<Typography variant="body2" color="text.secondary">
								אין נרשמים.
							</Typography>
						)}
					</Box>
				)}
				{tab === 'payments' && paymentsTable(rows.filter((r) => r.area === 'payment'))}
				{tab === 'admin' && !pid && auditTable()}
			</DialogContent>
			<DialogActions>
				<Button onClick={close}>סגירה</Button>
			</DialogActions>
		</Dialog>
	);
}
